/**
 * OnChain Wallet Selector Component
 *
 * A dropdown component that allows users to select which OnChain wallets
 * should be aggregated in the homepage metrics.
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import type { OnChainWallet } from '../../types/onchain';
import './OnChainWalletSelector.css';

interface OnChainWalletSelectorProps {
  wallets: OnChainWallet[];
  onToggleWallet: (walletId: string, include: boolean) => Promise<boolean>;
  isLoading?: boolean;
  totalValueUsd: number;
  includedCount: number;
}

export function OnChainWalletSelector({
  wallets,
  onToggleWallet,
  isLoading = false,
  totalValueUsd,
  includedCount,
}: OnChainWalletSelectorProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = async (walletId: string, currentValue: boolean) => {
    setUpdatingId(walletId);
    await onToggleWallet(walletId, !currentValue);
    setUpdatingId(null);
  };

  // Format wallet address for display
  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Format USD value
  const formatUsd = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    return `$${value.toFixed(0)}`;
  };

  if (wallets.length === 0) {
    return null;
  }

  return (
    <div className="onchain-wallet-selector" ref={dropdownRef}>
      <button
        className="onchain-wallet-selector__trigger"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
      >
        <Icons.Link size={16} />
        <span className="onchain-wallet-selector__label">
          {t('home.onchainWallets', 'OnChain Wallets')}
        </span>
        <span className="onchain-wallet-selector__count">
          {includedCount}/{wallets.length}
        </span>
        <span className="onchain-wallet-selector__value">
          {formatUsd(totalValueUsd)}
        </span>
        <Icons.ChevronDown
          size={14}
          className={`onchain-wallet-selector__chevron ${isOpen ? 'open' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="onchain-wallet-selector__dropdown">
          <div className="onchain-wallet-selector__header">
            <Icons.Wallet size={16} />
            <span>{t('home.selectWalletsToInclude', 'Select wallets to include')}</span>
          </div>

          <div className="onchain-wallet-selector__list">
            {wallets.map((wallet) => (
              <div
                key={wallet.id}
                className={`onchain-wallet-selector__item ${wallet.include_in_homepage ? 'included' : ''}`}
              >
                <button
                  className="onchain-wallet-selector__toggle"
                  onClick={() => handleToggle(wallet.id, wallet.include_in_homepage)}
                  disabled={updatingId === wallet.id}
                >
                  <div
                    className={`onchain-wallet-selector__switch ${wallet.include_in_homepage ? 'on' : 'off'}`}
                  >
                    {updatingId === wallet.id ? (
                      <div className="onchain-wallet-selector__spinner" />
                    ) : (
                      <div className="onchain-wallet-selector__switch-thumb" />
                    )}
                  </div>
                </button>

                <div className="onchain-wallet-selector__info">
                  <div className="onchain-wallet-selector__name">
                    {wallet.label || formatAddress(wallet.wallet_address)}
                    {wallet.is_primary && (
                      <span className="onchain-wallet-selector__primary-badge">
                        {t('home.primary', 'Primary')}
                      </span>
                    )}
                  </div>
                  <div className="onchain-wallet-selector__address">
                    {formatAddress(wallet.wallet_address)}
                  </div>
                </div>

                <div className="onchain-wallet-selector__value-col">
                  <span className="onchain-wallet-selector__wallet-value">
                    {formatUsd(wallet.total_value_usd || 0)}
                  </span>
                  {wallet.value_change_24h_percent !== undefined && (
                    <span
                      className={`onchain-wallet-selector__change ${
                        wallet.value_change_24h_percent >= 0 ? 'positive' : 'negative'
                      }`}
                    >
                      {wallet.value_change_24h_percent >= 0 ? '+' : ''}
                      {wallet.value_change_24h_percent.toFixed(1)}%
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="onchain-wallet-selector__footer">
            <Icons.Info size={12} />
            <span>{t('home.walletsWillBeAggregated', 'Selected wallets will be included in homepage metrics')}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default OnChainWalletSelector;
